import { useEffect, useMemo, useState } from "react";
import {
  ListMagnifyingGlass,
  CircleNotch,
  ArrowsClockwise,
  Coins,
  Article,
  SignIn,
  CaretDown,
  CaretRight,
  User,
  Download,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const ACTION_META = {
  "admin.login":         { Icon: SignIn,  tone: "text-[#3B5BDB]", label: "Admin sign-in" },
  "board_rate.update":   { Icon: Coins,   tone: "text-[#D4AF37]", label: "Board rate update" },
  "post.create":         { Icon: Article, tone: "text-[#16A34A]", label: "Post created" },
  "post.update":         { Icon: Article, tone: "text-[#3B5BDB]", label: "Post updated" },
  "post.delete":         { Icon: Article, tone: "text-[#DC2626]", label: "Post deleted" },
};

const meta = (a) =>
  ACTION_META[a] || { Icon: ListMagnifyingGlass, tone: "text-[#4A5568]", label: a };

export default function AdminAuditLogs() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async (action) => {
    setLoading(true);
    try {
      const url = action ? `/admin/audit-logs?action=${encodeURIComponent(action)}` : "/admin/audit-logs";
      const r = await api.get(url);
      setLogs(r.data.logs || []);
      setActions(r.data.actions || []);
    } catch {
      toast.error("Could not load audit logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(filter);
  }, [filter]);

  const exportCsv = () => {
    if (!logs.length) {
      toast.error("Nothing to export");
      return;
    }
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const headers = ["created_at", "action", "target", "target_id", "summary", "admin_email", "admin_name", "before", "after"];
    const rows = logs.map((l) => headers.map((h) => escape(l[h])).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mineai-audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${logs.length} entries`);
  };

  const grouped = useMemo(() => {
    const groups = {};
    logs.forEach((l) => {
      const date = new Date(l.created_at).toLocaleDateString("en-IN", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
      });
      (groups[date] = groups[date] || []).push(l);
    });
    return groups;
  }, [logs]);

  return (
    <div data-testid="admin-audit-logs-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            Audit Log
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Every admin action, recorded.
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            Who changed what and when. Useful for accountability and rollback.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="audit-action-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-10 px-3 pr-8 rounded-md border border-[#E8DFD1] bg-white text-sm outline-none focus:border-[#3B5BDB]"
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>
                {meta(a).label}
              </option>
            ))}
          </select>
          <button
            data-testid="audit-refresh"
            onClick={() => load(filter)}
            className="h-10 px-3 rounded-md border border-[#E8DFD1] hover:border-[#3B5BDB] smooth"
            title="Refresh"
          >
            <ArrowsClockwise size={16} />
          </button>
          <button
            data-testid="audit-export-csv"
            onClick={exportCsv}
            disabled={!logs.length}
            className="h-10 px-3 rounded-md bg-[#3B5BDB] text-white hover:bg-[#2F49AF] smooth disabled:opacity-50 flex items-center gap-1.5 text-sm font-medium"
          >
            <Download size={14} weight="duotone" /> Export CSV
          </button>
        </div>
      </div>

      <div className="mt-8">
        {loading ? (
          <div className="mc-card p-10 flex items-center gap-3 text-[#4A5568]">
            <CircleNotch className="animate-spin" weight="bold" /> Loading audit log...
          </div>
        ) : logs.length === 0 ? (
          <div className="mc-card p-10 text-center text-[#4A5568]" data-testid="audit-empty">
            No audit entries{filter ? ` for "${meta(filter).label}"` : ""} yet.
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(grouped).map(([date, items]) => (
              <div key={date}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#4A5568] font-semibold mb-3">
                  {date}
                </p>
                <div className="mc-card overflow-hidden">
                  <ul className="divide-y divide-[#E8DFD1]">
                    {items.map((l) => {
                      const m = meta(l.action);
                      const isOpen = expanded === l.id;
                      const hasDetail = l.before || l.after;
                      return (
                        <li
                          key={l.id}
                          className="p-4 sm:p-5"
                          data-testid={`audit-${l.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="h-9 w-9 rounded-full bg-[#F5EFE6] flex items-center justify-center shrink-0">
                              <m.Icon size={18} weight="duotone" className={m.tone} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                                <p className="text-sm text-[#0A1128]">
                                  <span className="font-medium">{l.summary || m.label}</span>
                                </p>
                                <p className="text-xs text-[#4A5568] tabular-nums shrink-0">
                                  {new Date(l.created_at).toLocaleTimeString("en-IN", {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </p>
                              </div>
                              <p className="text-xs text-[#4A5568] mt-1 flex items-center gap-1">
                                <User size={12} weight="duotone" />
                                {l.admin_name} · {l.admin_email}
                                <span className="opacity-60">·</span>
                                <code className="text-[10px] bg-[#F5EFE6] px-1.5 py-0.5 rounded">
                                  {l.action}
                                </code>
                              </p>
                              {hasDetail && (
                                <button
                                  data-testid={`audit-toggle-${l.id}`}
                                  onClick={() => setExpanded(isOpen ? null : l.id)}
                                  className="mt-2 text-xs text-[#3B5BDB] flex items-center gap-1 hover:opacity-80 smooth"
                                >
                                  {isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                                  {isOpen ? "Hide" : "Show"} change details
                                </button>
                              )}
                              {isOpen && hasDetail && (
                                <div className="mt-3 grid sm:grid-cols-2 gap-2">
                                  <DiffBox label="Before" data={l.before} tone="text-[#DC2626] bg-[#DC2626]/5" />
                                  <DiffBox label="After" data={l.after} tone="text-[#16A34A] bg-[#16A34A]/5" />
                                </div>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DiffBox({ label, data, tone }) {
  return (
    <div className={`rounded-md border border-[#E8DFD1] p-3 text-xs ${tone || ""}`}>
      <p className="text-[10px] uppercase tracking-wider font-semibold opacity-80 mb-1">
        {label}
      </p>
      {data ? (
        <pre className="whitespace-pre-wrap font-body text-[12px] text-[#0A1128]">
          {Object.entries(data)
            .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
            .join("\n")}
        </pre>
      ) : (
        <p className="text-[#4A5568] italic">—</p>
      )}
    </div>
  );
}
